import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Banner } from '@figma/fpl-components';
import { I18N_KEYS } from '@/language/keys';
import { CreditsContext } from '@/services/CreditsContext';
import { useSubscriptionPermissions } from '@/hooks/useSubscriptionPermissions/useSubscriptionPermissions';
import { PricingPageEntrypoint } from '@/enums/pricing-page.enum';
import { SubscriptionType } from '@/types/shared';

/** The canvas's not-enough-credits text, so the two places say the same thing. */
const CANVAS_MESSAGE = I18N_KEYS.RECIPE_MAIN.NODES.MODEL_BASE_NODE.MODEL_NODE_FOOTER.CREDITS_MESSAGE;

/**
 * Shown above the prompt when the balance can't pay for the Generate. Figma draws the version for someone who
 * can buy credits. The free plan and members who can't buy get the canvas's text, since those links would not
 * help them.
 */
export function GenerateCreditsBanner({ modelName }: { modelName: string }) {
  const { t } = useTranslation();
  const { handleGetMoreCreditsClick, openUpgradeModalFromEntrypoint } = useContext(CreditsContext);
  const { isSubscriptionTypeIn, isAllowedForPermission } = useSubscriptionPermissions();
  const isFreePlan = isSubscriptionTypeIn([SubscriptionType.Free]);
  const canBuyCredits = isAllowedForPermission('credits', 'action');
  const openUpgrade = () =>
    openUpgradeModalFromEntrypoint({ entrypoint: PricingPageEntrypoint.LowCredits, model: modelName });

  if (isFreePlan) {
    return (
      <Banner.Inset variant="default">
        <Banner.Message title={t(I18N_KEYS.GENERATE_PAGE.NOT_ENOUGH_CREDITS_TITLE)}>
          {t(CANVAS_MESSAGE.LOW_CREDITS_MESSAGE_PART_2_FREE)}
        </Banner.Message>
        <Banner.Button onClick={openUpgrade}>{t(I18N_KEYS.PAYMENTS.PRICING_TABLE.UPGRADE_CTA)}</Banner.Button>
      </Banner.Inset>
    );
  }
  if (!canBuyCredits) {
    return (
      <Banner.Inset variant="default">
        <Banner.Message title={t(I18N_KEYS.GENERATE_PAGE.NOT_ENOUGH_CREDITS_TITLE)}>
          {t(CANVAS_MESSAGE.LOW_CREDITS_MESSAGE_PART_2_MEMBER)}
        </Banner.Message>
      </Banner.Inset>
    );
  }
  return (
    <Banner.Inset variant="default">
      <Banner.Message title={t(I18N_KEYS.GENERATE_PAGE.NOT_ENOUGH_CREDITS_TITLE)}>
        <Banner.ButtonLink onClick={handleGetMoreCreditsClick}>
          {t(CANVAS_MESSAGE.LOW_CREDITS_MESSAGE_PART_2_GET_MORE_CREDITS_CTA)}
        </Banner.ButtonLink>{' '}
        {t(CANVAS_MESSAGE.LOW_CREDITS_MESSAGE_OR)}{' '}
        <Banner.ButtonLink onClick={openUpgrade}>{t(I18N_KEYS.GENERATE_PAGE.UPGRADE_LINK)}</Banner.ButtonLink>
      </Banner.Message>
      <Banner.Button onClick={openUpgrade}>{t(I18N_KEYS.PAYMENTS.PRICING_TABLE.UPGRADE_CTA)}</Banner.Button>
    </Banner.Inset>
  );
}
